import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import {
  Building2,
  Search,
  ArrowLeft,
  Loader2,
  MapPin,
  Phone,
  Globe,
  Mail,
  Star,
  User,
  Linkedin,
  ExternalLink,
  CheckCircle2,
  Copy,
  Save,
  Upload,
  FileText,
  Play,
  Download,
  Users,
  ChevronDown,
  ChevronUp,
  Shield,
  Sparkles,
  List
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyLookupPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState('single'); // 'single' | 'batch' | 'vat'

  // Single search
  const [companyName, setCompanyName] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Legge query params ?name=&location= per auto-precompilazione da VIES + auto-run
  const locationHook = useLocation();
  const autoSearchDoneRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(locationHook.search);
    const n = params.get('name');
    const l = params.get('location');
    const auto = params.get('auto');
    if (n) {
      setCompanyName(n);
      setLocation(l || '');
      setMode('single');
      if (auto === '1' && !autoSearchDoneRef.current) {
        autoSearchDoneRef.current = true;
        // Lancia la ricerca dopo che gli state sono aggiornati
        setTimeout(() => {
          document.querySelector('[data-testid="search-company-btn"]')?.click();
        }, 200);
      }
    }
  }, [locationHook.search]);

  // Batch search
  const [batchText, setBatchText] = useState('');
  const [parsedNames, setParsedNames] = useState([]);
  const [batchLocation, setBatchLocation] = useState('');
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const pollRef = useRef(null);

  const parseNames = (text) => {
    return text.split('\n').map(l => l.trim()).filter(l => l.length > 2);
  };

  const handleBatchTextChange = (text) => {
    setBatchText(text);
    setParsedNames(parseNames(text));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/company/upload-names`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const names = res.data.names || [];
      const combined = [...new Set([...parsedNames, ...names])];
      setParsedNames(combined);
      setBatchText(prev => prev + (prev ? '\n' : '') + names.join('\n'));
      toast.success(`${names.length} nomi estratti da ${file.name}`);
    } catch {
      toast.error('Errore nel caricamento del file');
    }
    e.target.value = '';
  };

  // Single search
  const handleSearch = async () => {
    if (!companyName.trim()) { toast.error("Inserisci il nome dell'azienda"); return; }
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API_URL}/company/lookup`, {
        company_name: companyName.trim(),
        location: location.trim() || null
      });
      setResult(response.data);
      toast[response.data.found ? 'success' : 'warning'](response.data.found ? 'Azienda trovata!' : 'Azienda non trovata');
    } catch {
      toast.error('Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  };

  // Batch search
  const startBatch = async () => {
    if (parsedNames.length === 0) { toast.error('Inserisci almeno un nome azienda'); return; }
    setLoading(true);
    setJob(null);
    try {
      const res = await axios.post(`${API_URL}/company/batch-lookup`, {
        company_names: parsedNames,
        location: batchLocation.trim() || null
      });
      setJobId(res.data.job_id);
      toast.success(`Ricerca avviata per ${parsedNames.length} aziende`);
    } catch {
      toast.error("Errore nell'avvio della ricerca");
      setLoading(false);
    }
  };

  const pollStatus = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API_URL}/scraper/status/${jobId}`);
      setJob(res.data);
      if (res.data.status === 'completed' || res.data.status === 'failed') {
        setLoading(false);
        clearInterval(pollRef.current);
        if (res.data.status === 'completed') {
          const found = (res.data.results || []).filter(r => r.found).length;
          toast.success(`Completato! ${found}/${res.data.results?.length} aziende trovate`);
        }
      }
    } catch { /* ignore */ }
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      pollRef.current = setInterval(pollStatus, 3000);
      pollStatus();
      return () => clearInterval(pollRef.current);
    }
  }, [jobId, pollStatus]);

  const exportCSV = async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API_URL}/scraper/results/${jobId}/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `company_lookup_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success('CSV scaricato!');
    } catch {
      toast.error('Errore export');
    }
  };

  const exportEmailsCSV = async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API_URL}/scraper/results/${jobId}/emails-csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `emails_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success('Email esportate in CSV!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nessuna email da esportare');
    }
  };

  const exportEmailsTXT = async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API_URL}/scraper/results/${jobId}/emails-txt`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `emails_${new Date().toISOString().slice(0, 10)}.txt`;
      a.click();
      toast.success('Lista email scaricata!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nessuna email da esportare');
    }
  };

  const saveAllAsLeads = async () => {
    if (!jobId) return;
    try {
      const res = await axios.post(`${API_URL}/scraper/save-to-leads/${jobId}`);
      toast.success(res.data.message);
    } catch {
      toast.error('Errore nel salvataggio');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato!');
  };

  const saveAsLead = async () => {
    if (!result?.data) return;
    try {
      await axios.post(`${API_URL}/leads`, {
        name: result.data.name,
        company: result.data.name,
        phone: result.data.phone,
        website: result.data.website,
        location: result.data.address?.split(',')[0] || location,
        size: 'medium',
        email: result.hunter_emails?.[0]?.email || result.web_emails?.[0] || null,
        source: 'company_lookup'
      });
      toast.success('Salvato come lead!');
    } catch {
      toast.error('Errore nel salvataggio');
    }
  };

  const batchResults = job?.results || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <input ref={fileInputRef} type="file" accept=".txt,.csv" className="hidden" onChange={handleFileUpload} />

      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center">
                <Building2 className="w-5 h-5" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Cerca Azienda</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Mode toggle */}
        <div className="flex flex-wrap gap-2" data-testid="mode-toggle">
          <button
            onClick={() => setMode('single')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition font-medium text-sm ${
              mode === 'single' ? 'border-purple-500 bg-purple-50 text-purple-800' : 'border-slate-200 text-slate-500 hover:border-slate-300'
            }`}
            data-testid="mode-single"
          >
            <Search className="w-4 h-4" /> Ricerca Singola
          </button>
          <button
            onClick={() => setMode('batch')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition font-medium text-sm ${
              mode === 'batch' ? 'border-purple-500 bg-purple-50 text-purple-800' : 'border-slate-200 text-slate-500 hover:border-slate-300'
            }`}
            data-testid="mode-batch"
          >
            <List className="w-4 h-4" /> Lista Aziende
          </button>
          <button
            onClick={() => setMode('vat')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition font-medium text-sm ${
              mode === 'vat' ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-200 text-slate-500 hover:border-slate-300'
            }`}
            data-testid="mode-vat"
          >
            <Shield className="w-4 h-4" /> Cerca da P.IVA
          </button>
        </div>

        {mode === 'vat' && <VatLookupSection />}

        {/* ========== SINGLE MODE ========== */}
        {mode === 'single' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" /> Cerca Azienda per Nome
                </CardTitle>
                <CardDescription>Inserisci il nome esatto dell'azienda per trovare tutti i suoi contatti</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 space-y-2">
                    <Label>Nome Azienda *</Label>
                    <Input placeholder="es. Gandolfi e Mura Architetti" value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()}
                      data-testid="company-name-input" />
                  </div>
                  <div className="sm:w-48 space-y-2">
                    <Label>Località (opzionale)</Label>
                    <Input placeholder="es. Bergamo" value={location}
                      onChange={(e) => setLocation(e.target.value)} data-testid="company-location-input" />
                  </div>
                  <div className="sm:w-auto sm:self-end">
                    <Button onClick={handleSearch} disabled={loading || !companyName.trim()} data-testid="search-company-btn">
                      {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ricerca...</> : <><Search className="w-4 h-4 mr-2" /> Cerca</>}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Single result */}
        {mode === 'single' && result?.found && (
          <SingleResult result={result} copyToClipboard={copyToClipboard} saveAsLead={saveAsLead} />
        )}
        {mode === 'single' && result && !result.found && (
          <Card className="border-slate-200"><CardContent className="p-8 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Azienda non trovata. Prova con un nome diverso.</p>
          </CardContent></Card>
        )}

        {/* ========== BATCH MODE ========== */}
        {mode === 'batch' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <List className="w-5 h-5" /> Lista Aziende da Cercare
                </CardTitle>
                <CardDescription>
                  Inserisci una lista di nomi aziendali (uno per riga) oppure carica un file TXT/CSV
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Nomi aziende (uno per riga)</Label>
                  <textarea
                    value={batchText}
                    onChange={(e) => handleBatchTextChange(e.target.value)}
                    placeholder={"Studio Legale Rossi\nCommercialista Bianchi\nArch. Verdi & Associati\nElettricista Bergamasca SRL"}
                    className="w-full h-40 rounded-lg border border-slate-300 p-3 text-sm font-mono resize-y focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                    data-testid="batch-names-textarea"
                  />
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { fileInputRef.current.accept = '.txt'; fileInputRef.current.click(); }}
                    data-testid="upload-txt-btn">
                    <Upload className="w-4 h-4 mr-2" /> Carica TXT
                  </Button>
                  <Button variant="outline" onClick={() => { fileInputRef.current.accept = '.csv'; fileInputRef.current.click(); }}
                    data-testid="upload-csv-btn">
                    <FileText className="w-4 h-4 mr-2" /> Carica CSV
                  </Button>
                </div>

                {parsedNames.length > 0 && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-purple-700">{parsedNames.length} aziende da cercare</span>
                      <button onClick={() => { setBatchText(''); setParsedNames([]); }} className="text-xs text-purple-400 hover:text-purple-600">Pulisci</button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {parsedNames.slice(0, 15).map((name, i) => (
                        <Badge key={i} variant="secondary" className="text-xs py-1">{name}</Badge>
                      ))}
                      {parsedNames.length > 15 && <Badge variant="outline" className="text-xs">+{parsedNames.length - 15} altre</Badge>}
                    </div>
                  </div>
                )}

                <div className="sm:w-48 space-y-2">
                  <Label>Località (opzionale, comune a tutte)</Label>
                  <Input placeholder="es. Bergamo" value={batchLocation}
                    onChange={(e) => setBatchLocation(e.target.value)} data-testid="batch-location-input" />
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <span className="text-sm text-slate-500">
                    {parsedNames.length > 0 ? `~${Math.ceil(parsedNames.length * 0.5)} min stimati` : 'Inserisci nomi per iniziare'}
                  </span>
                  <Button onClick={startBatch} disabled={loading || parsedNames.length === 0}
                    className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="start-batch-lookup-btn">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> In corso...</> : <><Play className="w-4 h-4 mr-2" /> Avvia Ricerca</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Batch progress */}
        {mode === 'batch' && job && job.status !== 'completed' && job.status !== 'failed' && (
          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                <span className="font-semibold text-purple-800">Ricerca in corso...</span>
              </div>
              {job.current_task && <p className="text-sm text-purple-700 mb-2">{job.current_task}</p>}
              <div className="text-sm text-purple-700">Aziende elaborate: {job.processed || 0}/{job.total || parsedNames.length}</div>
              <div className="mt-3 h-2 bg-purple-200 rounded-full overflow-hidden">
                <div className="h-full bg-purple-600 rounded-full transition-all duration-500"
                  style={{ width: `${job.total ? (job.processed || 0) / job.total * 100 : 0}%` }} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Batch results */}
        {mode === 'batch' && job?.status === 'completed' && batchResults.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900" data-testid="batch-results-title">
                {batchResults.filter(r => r.found).length}/{batchResults.length} aziende trovate
              </h2>
              <div className="flex flex-wrap gap-2">
                <Button onClick={exportCSV} variant="outline" data-testid="export-csv-btn"><Download className="w-4 h-4 mr-2" /> CSV Completo</Button>
                <Button onClick={exportEmailsCSV} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="export-emails-csv-btn">
                  <Mail className="w-4 h-4 mr-2" /> Email CSV
                </Button>
                <Button onClick={exportEmailsTXT} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="export-emails-txt-btn">
                  <FileText className="w-4 h-4 mr-2" /> Email TXT
                </Button>
                <Button onClick={saveAllAsLeads} data-testid="save-all-leads-btn"><Save className="w-4 h-4 mr-2" /> Salva tutti</Button>
              </div>
            </div>

            {batchResults.map((company, idx) => {
              const isExpanded = expandedCompany === idx;
              const data = company.data || {};
              const network = company.linkedin_network || {};

              return (
                <Card key={idx} className={`border-slate-200 overflow-hidden ${!company.found ? 'opacity-60' : ''}`}
                  data-testid={`batch-company-${idx}`}>
                  <button onClick={() => setExpandedCompany(isExpanded ? null : idx)}
                    className="w-full text-left p-4 hover:bg-slate-50/50 transition" data-testid={`expand-batch-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${company.found ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-400'}`}>
                          <Building2 className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-semibold text-slate-900 truncate">{data.name || company.company_name}</h3>
                            <CopyBtn value={data.name || company.company_name} copyToClipboard={copyToClipboard} testId={`copy-batch-name-${idx}`} label="nome" />
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
                            {data.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {data.address}</span>}
                            {data.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {data.phone}</span>}
                            {data.website && <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {data.website}</span>}
                            {data.rating && <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {data.rating}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {company.found ? (
                          <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Trovata</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500">Non trovata</Badge>
                        )}
                        {company.decision_makers?.length > 0 && (
                          <Badge className="bg-purple-100 text-purple-700"><Users className="w-3 h-3 mr-1" /> {company.decision_makers.length}</Badge>
                        )}
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>
                  </button>

                  {isExpanded && company.found && (
                    <div className="border-t border-slate-100">
                      {/* Company info with copy buttons */}
                      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50/40 border-b border-slate-100">
                        <InfoField iconEl={<Building2 className="w-4 h-4" />} label="Nome" value={data.name} copyToClipboard={copyToClipboard} testId={`batch-name-${idx}`} />
                        <InfoField iconEl={<MapPin className="w-4 h-4" />} label="Indirizzo" value={data.address} copyToClipboard={copyToClipboard} testId={`batch-address-${idx}`} />
                        <InfoField iconEl={<Phone className="w-4 h-4" />} label="Telefono" value={data.phone} copyToClipboard={copyToClipboard} testId={`batch-phone-${idx}`} />
                        <InfoField iconEl={<Globe className="w-4 h-4" />} label="Sito Web" value={data.website} href={data.website} copyToClipboard={copyToClipboard} testId={`batch-website-${idx}`} />
                        <div className="sm:col-span-2 flex justify-end">
                          <Button variant="outline" size="sm" onClick={() => {
                            const lines = [
                              data.name && `Nome: ${data.name}`,
                              data.address && `Indirizzo: ${data.address}`,
                              data.phone && `Telefono: ${data.phone}`,
                              data.website && `Sito: ${data.website}`,
                              company.pec_probable && `PEC: ${company.pec_probable}`,
                            ].filter(Boolean).join('\n');
                            copyToClipboard(lines);
                          }} data-testid={`copy-all-batch-${idx}`}>
                            <Copy className="w-3.5 h-3.5 mr-2" /> Copia Tutto
                          </Button>
                        </div>
                      </div>
                      {/* Decision makers / Hunter emails */}
                      {(company.decision_makers?.length > 0 || company.hunter_emails?.length > 0) && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid={`referents-table-${idx}`}>
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="text-left py-2 px-4 font-semibold text-slate-600 text-xs">Referente</th>
                                <th className="text-left py-2 px-4 font-semibold text-slate-600 text-xs">Ruolo</th>
                                <th className="text-left py-2 px-4 font-semibold text-slate-600 text-xs">Email</th>
                                <th className="text-left py-2 px-4 font-semibold text-slate-600 text-xs">Confidence</th>
                                <th className="text-left py-2 px-4 font-semibold text-slate-600 text-xs">Azioni</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(company.decision_makers?.length > 0 ? company.decision_makers : company.hunter_emails || []).map((ref, rIdx) => (
                                <tr key={rIdx} className="border-b border-slate-100">
                                  <td className="py-2 px-4 font-medium text-sm">{ref.name || `${ref.first_name || ''} ${ref.last_name || ''}`.trim() || '-'}</td>
                                  <td className="py-2 px-4 text-sm text-slate-600">{ref.position || '-'}</td>
                                  <td className="py-2 px-4 font-mono text-xs">{ref.email || '-'}</td>
                                  <td className="py-2 px-4">
                                    {ref.confidence ? (
                                      <Badge className="bg-amber-100 text-amber-700 text-xs"><Shield className="w-3 h-3 mr-1" /> {ref.confidence}%</Badge>
                                    ) : '-'}
                                  </td>
                                  <td className="py-2 px-4">
                                    <div className="flex gap-1">
                                      {ref.email && <Button variant="ghost" size="sm" onClick={() => copyToClipboard(ref.email)}><Copy className="w-3 h-3" /></Button>}
                                      {ref.linkedin && <a href={ref.linkedin} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm"><Linkedin className="w-3 h-3" /></Button></a>}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Web emails + LinkedIn + PEC */}
                      <div className="p-4 space-y-3">
                        {company.web_emails?.length > 0 && (
                          <div>
                            <span className="text-xs font-semibold text-slate-500 block mb-1">Email dal sito:</span>
                            <div className="flex flex-wrap gap-1.5">
                              {company.web_emails.map((email, i) => {
                                const emailStr = typeof email === 'object' ? email.email : email;
                                return emailStr ? (
                                  <Badge key={i} variant="secondary" className="text-xs font-mono cursor-pointer" onClick={() => copyToClipboard(emailStr)}>
                                    <Mail className="w-3 h-3 mr-1" /> {emailStr}
                                  </Badge>
                                ) : null;
                              })}
                            </div>
                          </div>
                        )}
                        {company.pec_probable && (
                          <div>
                            <span className="text-xs font-semibold text-slate-500 block mb-1">PEC probabile:</span>
                            <Badge className="bg-blue-100 text-blue-700 text-xs font-mono cursor-pointer" onClick={() => copyToClipboard(company.pec_probable)}>
                              <Mail className="w-3 h-3 mr-1" /> {company.pec_probable}
                            </Badge>
                          </div>
                        )}
                        {Object.keys(network).length > 0 && (
                          <div>
                            <span className="text-xs font-semibold text-slate-500 block mb-1">Rete LinkedIn:</span>
                            <div className="flex flex-wrap gap-2">
                              {network.company_search && <a href={network.company_search} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs h-7"><Building2 className="w-3 h-3 mr-1" /> Azienda <ExternalLink className="w-2.5 h-2.5 ml-1" /></Button></a>}
                              {network.people_at_company && <a href={network.people_at_company} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs h-7"><Users className="w-3 h-3 mr-1" /> Dipendenti <ExternalLink className="w-2.5 h-2.5 ml-1" /></Button></a>}
                              {network.ex_employees && <a href={network.ex_employees} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" className="border-purple-300 text-purple-700 hover:bg-purple-100 text-xs h-7"><Users className="w-3 h-3 mr-1" /> Ex-Dipendenti <ExternalLink className="w-2.5 h-2.5 ml-1" /></Button></a>}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </motion.div>
        )}
      </main>
    </div>
  );
};

// Reusable inline copy button
function CopyBtn({ value, copyToClipboard, testId, label }) {
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); copyToClipboard(value); }}
      className="inline-flex items-center justify-center w-6 h-6 rounded-md text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition flex-shrink-0"
      title={label ? `Copia ${label}` : 'Copia'}
      data-testid={testId}
    >
      <Copy className="w-3.5 h-3.5" />
    </button>
  );
}

// Field row for company data with copy button
function InfoField({ iconEl, label, value, href, copyToClipboard, testId }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 group" data-testid={`field-${testId}`}>
      <span className="text-slate-400 mt-0.5 flex-shrink-0">{iconEl}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <div className="flex items-center gap-1.5">
          {href ? (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-600 hover:underline truncate">{value}</a>
          ) : (
            <p className="text-sm font-medium truncate">{value}</p>
          )}
          <CopyBtn value={value} copyToClipboard={copyToClipboard} testId={`copy-${testId}`} label={label} />
        </div>
      </div>
    </div>
  );
}

// Single result component (extracted from old code)
function SingleResult({ result, copyToClipboard, saveAsLead }) {
  const data = result.data;
  const copyAll = () => {
    const lines = [
      data.name && `Nome: ${data.name}`,
      data.address && `Indirizzo: ${data.address}`,
      data.phone && `Telefono: ${data.phone}`,
      data.website && `Sito: ${data.website}`,
      result.pec_probable && `PEC: ${result.pec_probable}`,
    ].filter(Boolean).join('\n');
    copyToClipboard(lines);
  };
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-7 h-7 text-purple-600" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-xl truncate">{data.name}</CardTitle>
                  <CopyBtn value={data.name} copyToClipboard={copyToClipboard} testId="copy-name" label="nome" />
                  {result.source_used === 'paginegialle' && (
                    <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" title="Fonte: PagineGialle (fallback)">PG</span>
                  )}
                  {result.source_used === 'google_maps' && (
                    <span className="text-[10px] font-bold uppercase bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title="Fonte: Google Maps">GMaps</span>
                  )}
                </div>
                {data.category && <p className="text-slate-500 text-sm">{data.category}</p>}
                {data.rating && (
                  <div className="flex items-center gap-1 mt-1">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-medium text-sm">{data.rating}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={copyAll} data-testid="copy-all-btn">
                <Copy className="w-4 h-4 mr-2" /> Copia Tutto
              </Button>
              <Button onClick={saveAsLead} data-testid="save-lead-btn"><Save className="w-4 h-4 mr-2" /> Salva come Lead</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InfoField iconEl={<MapPin className="w-4 h-4" />} label="Indirizzo" value={data.address} copyToClipboard={copyToClipboard} testId="address" />
          <InfoField iconEl={<Phone className="w-4 h-4" />} label="Telefono" value={data.phone} copyToClipboard={copyToClipboard} testId="phone" />
          <InfoField iconEl={<Globe className="w-4 h-4" />} label="Sito Web" value={data.website} href={data.website} copyToClipboard={copyToClipboard} testId="website" />
          <InfoField iconEl={<Mail className="w-4 h-4" />} label="PEC probabile" value={result.pec_probable} copyToClipboard={copyToClipboard} testId="pec" />
        </CardContent>
      </Card>

      {/* Hunter emails */}
      {result.hunter_emails?.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" /> Email Verificate (Hunter.io)</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {result.hunter_emails.map((he, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                  <div>
                    <span className="font-mono text-sm">{he.email}</span>
                    {he.first_name && <span className="text-xs text-slate-500 ml-2">({he.first_name} {he.last_name}{he.position ? ` - ${he.position}` : ''})</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {he.confidence && <Badge className="bg-amber-100 text-amber-700 text-xs">{he.confidence}%</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(he.email)}><Copy className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Decision makers */}
      {result.decision_makers?.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" /> Decision Makers</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {result.decision_makers.map((dm, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                  <div>
                    <span className="font-medium text-sm">{dm.name}</span>
                    {dm.position && <span className="text-xs text-slate-500 ml-2">— {dm.position}</span>}
                    <span className="font-mono text-xs text-slate-600 ml-2">{dm.email}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(dm.email)}><Copy className="w-3 h-3" /></Button>
                    {dm.linkedin && <a href={dm.linkedin} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm"><Linkedin className="w-3 h-3" /></Button></a>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* LinkedIn links */}
      {result.linkedin && Object.keys(result.linkedin).length > 0 && (
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Linkedin className="w-4 h-4" /> LinkedIn</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(result.linkedin).map(([key, url]) => (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs h-8">
                    <Linkedin className="w-3 h-3 mr-1.5" /> {key.replace(/_/g, ' ')} <ExternalLink className="w-2.5 h-2.5 ml-1" />
                  </Button>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}

export default CompanyLookupPage;

// ==================== VAT (P.IVA) Section ====================
function VatLookupSection() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [vat, setVat] = useState('');
  const [enrich, setEnrich] = useState(true);
  const [loading, setLoading] = useState(false);
  const [single, setSingle] = useState(null);
  const [batchResults, setBatchResults] = useState([]);
  const [batchText, setBatchText] = useState('');
  const [enrichJob, setEnrichJob] = useState(null);
  const [enrichPolling, setEnrichPolling] = useState(false);

  const copyToClipboard = (t) => {
    navigator.clipboard.writeText(t);
    toast.success('Copiato!');
  };

  const lookupOne = async () => {
    if (!vat.trim()) { toast.error('Inserisci una P.IVA'); return; }
    setLoading(true);
    setSingle(null);
    try {
      const res = await axios.post(`${API_URL}/vat/lookup`, { vat, enrich });
      setSingle(res.data);
      if (!res.data.vies?.valid) toast.error(res.data.vies?.error || 'P.IVA non valida');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore lookup');
    } finally {
      setLoading(false);
    }
  };

  const enrichNow = (companyName, city) => {
    // Naviga alla ricerca singola con nome+città precompilati + auto-run
    navigate(`/company-lookup?name=${encodeURIComponent(companyName)}&location=${encodeURIComponent(city || '')}&auto=1`);
    // Scrolla in cima per far vedere il risultato in arrivo
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const runBatchFromText = async () => {
    const vats = batchText.split(/[\n,;\s]+/).map(v => v.trim()).filter(Boolean);
    if (vats.length === 0) { toast.error('Nessuna P.IVA'); return; }
    if (vats.length > 100) { toast.error('Massimo 100 P.IVA'); return; }
    setLoading(true);
    setBatchResults([]);
    try {
      const res = await axios.post(`${API_URL}/vat/batch`, { vats });
      setBatchResults(res.data.results || []);
      const valid = (res.data.results || []).filter(r => r.valid).length;
      toast.success(`${valid}/${res.data.total} P.IVA valide`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore batch');
    } finally {
      setLoading(false);
    }
  };

  const runFromFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setBatchResults([]);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/vat/upload`, fd);
      setBatchResults(res.data.results || []);
      toast.success(`${res.data.total} P.IVA trovate nel file`);
      if (res.data.truncated) toast.info('File tagliato a 100 P.IVA');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload');
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const exportCSV = () => {
    if (batchResults.length === 0) return;
    const rows = [['P.IVA', 'Valida', 'Nome', 'Indirizzo', 'Errore']];
    batchResults.forEach(r => {
      rows.push([
        r.vat_number,
        r.valid ? 'SI' : 'NO',
        (r.name || '').replace(/[\n\r]+/g, ' '),
        (r.address || '').replace(/[\n\r]+/g, ' '),
        r.error || ''
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = window.URL.createObjectURL(new Blob(["\ufeff" + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `piva_lookup_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  // Batch VIES + arricchimento automatico
  const runBatchEnrich = async () => {
    const vats = batchText.split(/[\n,;\s]+/).map(v => v.trim()).filter(Boolean);
    if (vats.length === 0) { toast.error('Nessuna P.IVA'); return; }
    if (vats.length > 100) { toast.error('Massimo 100 P.IVA'); return; }
    setLoading(true);
    setEnrichJob(null);
    try {
      const res = await axios.post(`${API_URL}/vat/batch-enrich`, { vats });
      const { job_id, vies_summary, total } = res.data;
      setEnrichJob({ job_id, status: 'pending', processed: 0, total, results: [], vies_summary });
      setEnrichPolling(true);
      toast.success(`Job avviato: ${total} P.IVA valide su ${vies_summary.total_vats}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore avvio job');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enrichPolling || !enrichJob?.job_id) return;
    const t = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/scraper/status/${enrichJob.job_id}`);
        setEnrichJob(prev => ({ ...prev, ...res.data }));
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          setEnrichPolling(false);
          toast.success(res.data.status === 'completed' ? 'Arricchimento completato!' : 'Job terminato con errori');
        }
      } catch (e) { /* silent */ }
    }, 3000);
    return () => clearInterval(t);
  }, [enrichPolling, enrichJob?.job_id]);

  const exportEnrichCSV = async () => {
    if (!enrichJob?.job_id) return;
    try {
      const res = await axios.get(`${API_URL}/scraper/results/${enrichJob.job_id}/emails-csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `vat_enrich_emails_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      toast.success('Email esportate!');
    } catch (e) {
      toast.error('Errore export');
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" /> Ricerca Singola P.IVA
          </CardTitle>
          <CardDescription>
            Verifica una P.IVA su <b>VIES (registro ufficiale UE)</b> e recupera ragione sociale + indirizzo. Poi puoi arricchire con telefono/email/PEC.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-3">
            <Input
              placeholder="es. IT04353030168 oppure 04353030168"
              value={vat}
              onChange={e => setVat(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && lookupOne()}
              className="font-mono"
              data-testid="vat-input"
            />
            <Button onClick={lookupOne} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="vat-lookup-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Verifica su VIES
            </Button>
          </div>

          {single && (
            <div className={`p-4 rounded-lg border-2 ${single.vies?.valid ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`} data-testid="vat-result">
              {single.vies?.valid ? (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                      <CheckCircle2 className="w-5 h-5" /> P.IVA valida
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">{single.vies.country_code}{single.vies.vat_number}</Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <Building2 className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500">Ragione Sociale</p>
                        <p className="font-medium">{single.vies.name || '—'}</p>
                      </div>
                      {single.vies.name && (
                        <button onClick={() => copyToClipboard(single.vies.name)} className="text-slate-400 hover:text-emerald-600"><Copy className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500">Indirizzo</p>
                        <p className="font-medium whitespace-pre-line">{single.vies.address || '—'}</p>
                      </div>
                      {single.vies.address && (
                        <button onClick={() => copyToClipboard(single.vies.address)} className="text-slate-400 hover:text-emerald-600"><Copy className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                  {enrich && single.enrichment && (
                    <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center justify-between">
                      <p className="text-xs text-slate-600">Vuoi trovare anche telefono/email/PEC?</p>
                      <Button size="sm" onClick={() => enrichNow(single.enrichment.company_name, single.enrichment.city)} data-testid="enrich-vat-btn">
                        <Search className="w-3.5 h-3.5 mr-2" /> Arricchisci ora
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircleIcon /> {single.vies?.error || 'P.IVA non valida'}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <List className="w-5 h-5" /> Batch: Lista P.IVA (max 100)
          </CardTitle>
          <CardDescription>Incolla o carica un file TXT/CSV con una P.IVA per riga</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <textarea
            className="w-full min-h-[100px] p-3 rounded-md border border-slate-200 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
            placeholder="IT04353030168&#10;12345678901&#10;..."
            value={batchText}
            onChange={e => setBatchText(e.target.value)}
            data-testid="vat-batch-textarea"
          />
          <div className="flex flex-wrap gap-2">
            <Button onClick={runBatchFromText} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="vat-batch-run">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Solo verifica VIES
            </Button>
            <Button onClick={runBatchEnrich} disabled={loading || enrichPolling} className="bg-purple-600 hover:bg-purple-700" data-testid="vat-batch-enrich-btn">
              {(loading || enrichPolling) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Verifica + Arricchisci tutto
            </Button>
            <input ref={fileRef} type="file" accept=".csv,.txt" onChange={runFromFile} className="hidden" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={loading} data-testid="vat-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Carica CSV/TXT
            </Button>
            {batchResults.length > 0 && (
              <Button variant="outline" onClick={exportCSV} data-testid="vat-export-btn">
                <Download className="w-4 h-4 mr-2" /> Esporta CSV
              </Button>
            )}
          </div>

          {batchResults.length > 0 && (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[500px] overflow-y-auto" data-testid="vat-batch-results">
              {batchResults.map((r, i) => (
                <div key={i} className="p-3 flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${r.valid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                    {r.valid ? <CheckCircle2 className="w-4 h-4" /> : <ChevronDown className="w-4 h-4 rotate-45" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-slate-500">{r.country_code}{r.vat_number}</code>
                      {r.valid ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">VALIDA</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-600 text-[10px]">NON VALIDA</Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium mt-0.5 truncate">{r.name || r.error || '—'}</p>
                    {r.address && <p className="text-xs text-slate-500 whitespace-pre-line">{r.address}</p>}
                  </div>
                  {r.valid && r.name && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => enrichNow(r.name, '')}
                      className="flex-shrink-0"
                      data-testid={`enrich-vat-batch-${i}`}
                    >
                      <Search className="w-3.5 h-3.5 mr-1" /> Arricchisci
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Progress + risultati Enrichment automatico */}
          {enrichJob && (
            <div className="mt-2 border-2 border-purple-200 bg-purple-50/40 rounded-lg p-4 space-y-3" data-testid="enrich-progress">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-semibold text-slate-900">Arricchimento in corso</p>
                    <p className="text-xs text-slate-600">
                      {enrichJob.vies_summary?.valid || enrichJob.total} P.IVA valide · {enrichJob.vies_summary?.invalid || 0} scartate
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={enrichJob.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : enrichJob.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'}>
                    {enrichJob.status === 'completed' ? 'Completato' : enrichJob.status === 'failed' ? 'Errore' : (enrichJob.status || 'running')}
                  </Badge>
                  {enrichJob.status === 'completed' && (
                    <Button size="sm" variant="outline" onClick={exportEnrichCSV} data-testid="enrich-export-csv">
                      <Mail className="w-3.5 h-3.5 mr-1" /> Email CSV
                    </Button>
                  )}
                </div>
              </div>

              {enrichJob.status !== 'completed' && (
                <div>
                  <div className="w-full h-2 bg-purple-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-600 transition-all"
                      style={{ width: `${Math.round(((enrichJob.processed || 0) / Math.max(enrichJob.total || 1, 1)) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    {enrichJob.processed || 0}/{enrichJob.total} · {enrichJob.current_task || 'in coda...'}
                  </p>
                </div>
              )}

              {enrichJob.results && enrichJob.results.length > 0 && (
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[400px] overflow-y-auto bg-white" data-testid="enrich-results">
                  {enrichJob.results.map((r, i) => (
                    <div key={i} className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <code className="text-[10px] font-mono text-slate-500">{r.vat_number}</code>
                            {r.found ? (
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Trovata</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700 text-[10px]">Non trovata</Badge>
                            )}
                            {(r.hunter_emails?.length > 0) && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Hunter: {r.hunter_emails.length}</Badge>}
                            {r.pec_probable && <Badge className="bg-purple-100 text-purple-700 text-[10px]">PEC</Badge>}
                          </div>
                          <p className="text-sm font-medium mt-1 truncate">{r.company_name}</p>
                          {r.data && (
                            <div className="text-xs text-slate-500 space-y-0.5 mt-1">
                              {r.data.address && <p className="truncate">📍 {r.data.address}</p>}
                              {r.data.phone && <p>📞 {r.data.phone}</p>}
                              {r.data.website && <p className="truncate">🌐 {r.data.website}</p>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function AlertCircleIcon() {
  return <ExternalLink className="w-4 h-4" />;
}
